import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const SupportSection = ({ supportTickets, onCreateTicket, onUpdateTicket }) => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    subject: '',
    category: 'general',
    priority: 'medium',
    description: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    onCreateTicket(formData);
    setShowCreateForm(false);
    setFormData({
      subject: '',
      category: 'general',
      priority: 'medium',
      description: ''
    });
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'open':
        return 'text-primary bg-primary/10 border-primary/30';
      case 'in-progress':
        return 'text-accent bg-accent/10 border-accent/30';
      case 'resolved':
        return 'text-success bg-success/10 border-success/30';
      case 'closed':
        return 'text-muted-foreground bg-muted/10 border-border';
      default:
        return 'text-muted-foreground bg-muted/10 border-border';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high':
        return 'text-error';
      case 'medium':
        return 'text-accent';
      case 'low':
        return 'text-success';
      default:
        return 'text-muted-foreground';
    }
  };

  const quickActions = [
    {
      title: 'Track Your Order',
      description: 'Get real-time updates on your order status',
      icon: 'Package',
      action: 'track-order'
    },
    {
      title: 'Return & Refunds',
      description: 'Initiate returns or check refund status',
      icon: 'RotateCcw',
      action: 'returns'
    },
    {
      title: 'Product Support',
      description: 'Get help with product setup and troubleshooting',
      icon: 'HelpCircle',
      action: 'product-support'
    },
    {
      title: 'Account Issues',
      description: 'Resolve login, password, or account problems',
      icon: 'User',
      action: 'account-issues'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Quick Actions */}
      <div className="holographic-card p-6 rounded-lg">
        <h2 className="text-xl font-bold command-text mb-6">Quick Support Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {quickActions?.map((action) => (
            <div
              key={action?.action}
              className="p-4 border border-border rounded-lg hover:bg-muted/20 cursor-pointer transition-all duration-200"
            >
              <div className="flex items-start space-x-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Icon name={action?.icon} size={20} className="text-primary" />
                </div>
                <div>
                  <h3 className="font-medium text-foreground mb-1">{action?.title}</h3>
                  <p className="text-sm text-muted-foreground">{action?.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Support Tickets */}
      <div className="holographic-card p-6 rounded-lg">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold command-text">Support Tickets</h2>
          <Button
            variant="default"
            size="sm"
            onClick={() => setShowCreateForm(true)}
            iconName="Plus"
            iconPosition="left"
          >
            Create Ticket
          </Button>
        </div>

        {/* Create Ticket Form */}
        {showCreateForm && (
          <div className="mb-6 p-4 border border-border rounded-lg bg-muted/20">
            <h3 className="text-lg font-semibold mb-4">Create Support Ticket</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                label="Subject"
                name="subject"
                value={formData?.subject}
                onChange={handleInputChange}
                placeholder="Brief description of your issue"
                required
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Category</label>
                  <select
                    name="category"
                    value={formData?.category}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 bg-input border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                  >
                    <option value="general">General Inquiry</option>
                    <option value="order">Order Issues</option>
                    <option value="product">Product Support</option>
                    <option value="account">Account Problems</option>
                    <option value="technical">Technical Issues</option>
                    <option value="billing">Billing & Payments</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Priority</label>
                  <select
                    name="priority"
                    value={formData?.priority}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 bg-input border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <textarea
                  name="description"
                  value={formData?.description}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-3 py-2 bg-input border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                  placeholder="Please provide detailed information about your issue..."
                  required
                />
              </div>

              <div className="flex space-x-2">
                <Button type="submit" variant="default">
                  Create Ticket
                </Button>
                <Button 
                  type="button" 
                  variant="ghost" 
                  onClick={() => setShowCreateForm(false)}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Tickets List */}
        <div className="space-y-4">
          {supportTickets?.length === 0 ? (
            <div className="text-center py-8">
              <Icon name="MessageSquare" size={48} className="text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No support tickets yet</p>
            </div>
          ) : (
            supportTickets?.map((ticket) => (
              <div
                key={ticket?.id}
                className="border border-border rounded-lg p-4 hover:bg-muted/20 transition-all duration-200"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center space-x-3 mb-2">
                      <span className="font-mono text-sm text-muted-foreground">#{ticket?.id}</span>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(ticket?.status)}`}
                      >
                        {ticket?.status}
                      </span>
                      <span className={`text-xs font-medium ${getPriorityColor(ticket?.priority)}`}>
                        {ticket?.priority?.toUpperCase()} PRIORITY
                      </span>
                    </div>
                    <h3 className="font-medium text-foreground mb-1">{ticket?.subject}</h3>
                    <p className="text-sm text-muted-foreground mb-2">{ticket?.description}</p>
                    <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                      <span>Category: {ticket?.category}</span>
                      <span>Created: {new Date(ticket.createdAt)?.toLocaleDateString()}</span>
                      {ticket?.lastUpdated && (
                        <span>Updated: {new Date(ticket.lastUpdated)?.toLocaleDateString()}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="MessageSquare"
                      iconPosition="left"
                    >
                      View
                    </Button>
                    {ticket?.status !== 'closed' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="X"
                        iconPosition="left"
                        className="text-error hover:text-error"
                      >
                        Close
                      </Button>
                    )}
                  </div>
                </div>
                
                {ticket?.lastResponse && (
                  <div className="mt-3 p-3 bg-muted/20 rounded-lg">
                    <p className="text-sm text-muted-foreground mb-1">Latest Response:</p>
                    <p className="text-sm text-foreground">{ticket?.lastResponse}</p>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
      {/* Contact Information */}
      <div className="holographic-card p-6 rounded-lg">
        <h2 className="text-xl font-bold command-text mb-4">Contact Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="p-3 bg-primary/10 rounded-lg w-fit mx-auto mb-3">
              <Icon name="Phone" size={24} className="text-primary" />
            </div>
            <h3 className="font-medium mb-1">Phone Support</h3>
            <p className="text-sm text-muted-foreground mb-2">1-800-ELECTRO</p>
            <p className="text-xs text-muted-foreground">Mon-Fri 9AM-6PM EST</p>
          </div>
          <div className="text-center">
            <div className="p-3 bg-secondary/10 rounded-lg w-fit mx-auto mb-3">
              <Icon name="Mail" size={24} className="text-secondary" />
            </div>
            <h3 className="font-medium mb-1">Email Support</h3>
            <p className="text-sm text-muted-foreground mb-2">support@electroshoppro.com</p>
            <p className="text-xs text-muted-foreground">Response within 24 hours</p>
          </div>
          <div className="text-center">
            <div className="p-3 bg-accent/10 rounded-lg w-fit mx-auto mb-3">
              <Icon name="MessageCircle" size={24} className="text-accent" />
            </div>
            <h3 className="font-medium mb-1">Live Chat</h3>
            <p className="text-sm text-muted-foreground mb-2">Available 24/7</p>
            <Button variant="outline" size="sm">Start Chat</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportSection;